import React, { Component } from 'react';
import { Box, Alert } from '@mui/material';

import { StyledButton } from '../../styles/StyledButtons';
import StyledModal from '../../styles/StyledModal';
import { StyledTypography } from '../../styles/styles';

interface DeleteOperationsProps {
  fundOpportunity: any;
  open: boolean;
  submitting?: boolean;
  error?: string;
  warning?: {
    relatedEntities: {
      investors: Array<{ status: string; count: number }>;
      addresses: { count: number };
      documents: { count: number };
      irrUpdates: { count: number };
      linkedContentReferences: { count: number };
    };
  };
  onClose: () => void;
  onDelete: (force?: boolean) => void;
}

class DeleteOperations extends Component<DeleteOperationsProps> {
  renderWarningContent = () => {
    const { warning } = this.props;

    if (!warning?.relatedEntities) {
      return null;
    }

    const { relatedEntities } = warning;
    const totalInvestors = relatedEntities.investors.reduce((sum, inv) => sum + inv.count, 0);

    return (
      <Alert severity="warning" sx={{ mt: 2, mb: 2 }}>
        <StyledTypography tone="warning" sx={{ mb: 1 }}>
          This fund has related data that will be permanently deleted:
        </StyledTypography>
        <Box component="ul" sx={{ margin: '8px 0', paddingLeft: '24px' }}>
          {totalInvestors > 0 && (
            <StyledTypography tone="warning" component="li">
              {totalInvestors} investor(s)
              {relatedEntities.investors.map((inv) => (
                <span key={inv.status}>
                  {' '}
                  - {inv.count} {inv.status.toLowerCase()}
                </span>
              ))}
            </StyledTypography>
          )}
          {relatedEntities.addresses.count > 0 && (
            <StyledTypography tone="warning" component="li">
              {relatedEntities.addresses.count} address(es)
            </StyledTypography>
          )}
          {relatedEntities.documents.count > 0 && (
            <StyledTypography tone="warning" component="li">
              {relatedEntities.documents.count} document(s)
            </StyledTypography>
          )}
          {relatedEntities.irrUpdates.count > 0 && (
            <StyledTypography tone="warning" component="li">
              {relatedEntities.irrUpdates.count} IRR update(s)
            </StyledTypography>
          )}
          {relatedEntities.linkedContentReferences.count > 0 && (
            <StyledTypography tone="warning" component="li">
              {relatedEntities.linkedContentReferences.count} linked content reference(s)
            </StyledTypography>
          )}
        </Box>
        <StyledTypography tone="warning" sx={{ mt: 1 }}>
          All related entities will be permanently deleted. This action cannot be undone.
        </StyledTypography>

        {/* Confirmation buttons for force delete */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
          <StyledButton
            noTopSpacing
            buttonVariant="inactiveOrCancel"
            onClick={this.props.onClose}
            disabled={this.props.submitting}
          >
            Cancel
          </StyledButton>
          <StyledButton
            noTopSpacing
            buttonVariant="warning"
            onClick={() => this.props.onDelete(true)}
            disabled={this.props.submitting}
          >
            {this.props.submitting ? 'Deleting...' : 'Delete Anyway'}
          </StyledButton>
        </Box>
      </Alert>
    );
  };

  render() {
    const { fundOpportunity, open, submitting, error, warning, onClose, onDelete } = this.props;

    if (!open) {
      return null;
    }

    return (
      <StyledModal
        open={open}
        onClose={onClose}
        title="Delete Fund"
        actions={
          !warning && (
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <StyledButton
                noTopSpacing
                buttonVariant="inactiveOrCancel"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </StyledButton>
              <StyledButton
                noTopSpacing
                buttonVariant="warning"
                onClick={() => onDelete(false)}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete'}
              </StyledButton>
            </Box>
          )
        }
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {this.renderWarningContent() || (
          <StyledTypography tone="tone1">
            Are you sure you want to delete the fund &quot;{fundOpportunity.name}&quot;?
          </StyledTypography>
        )}

        {!warning && (
          <StyledTypography tone="tone1" sx={{ mt: 2 }}>
            This action will permanently delete the fund and all related data. This cannot be
            undone.
          </StyledTypography>
        )}
      </StyledModal>
    );
  }
}

export default DeleteOperations;
